import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { Layers, Loader2, Pin, EyeOff } from 'lucide-react';
import { useCreativeStrategyStore } from '@/stores/creativeStrategy';
import { creativeStrategyApi, type StrategyDocketSummary } from '@/services/api';
import { LibraryBrowser } from '@/components/knowledge/creative-strategy/LibraryBrowser';

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

// Naming: a Library is the category level (stored as a "docket" record), shared by
// every client in the category. A Docket is one client's working set (stored as its
// pack plus overrides). A pack is the versioned snapshot behind either.
export function CreativeStrategyIndexPage({ view = 'libraries' }: { view?: 'libraries' | 'dockets' }) {
  const { packs, loading, error, fetchPacks } = useCreativeStrategyStore();
  const [dockets, setDockets] = useState<StrategyDocketSummary[]>([]);
  const [docketsLoading, setDocketsLoading] = useState(true);
  const [docketError, setDocketError] = useState<string | null>(null);
  const showDockets = view === 'libraries';
  const showPacks = view === 'dockets';

  useEffect(() => {
    if (showPacks) fetchPacks();
    // Both views need the libraries: the client view shows which one each client draws on.
    setDocketsLoading(true);
    creativeStrategyApi.listDockets()
      .then((res) => setDockets(res.dockets))
      .catch((err: Error) => setDocketError(err.message))
      .finally(() => setDocketsLoading(false));
  }, [fetchPacks, showPacks]);

  const libraryOf = (clientUid: string) =>
    dockets.filter((d) => d.member_clients.includes(clientUid));

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-navy-900 flex items-center justify-center text-white">
            <Layers size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-navy-900">
              {showPacks ? 'Dockets' : 'Library'}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {showPacks
                ? "Each client's working set: the library it draws from, its own mined evidence, and the pins and hides set for it."
                : 'One library per category: the evidence and everything mined from it, shared by every client that sells in it.'}
            </p>
          </div>
        </div>
      </div>

      {showDockets && (
        <div className="flex-1 min-h-0">
          {docketError && (
            <div className="m-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{docketError}</div>
          )}
          <LibraryBrowser libraries={dockets} librariesLoading={docketsLoading} />
        </div>
      )}
      <div className={clsx('flex-1 overflow-auto p-6', showDockets && 'hidden')}>
        {showDockets && docketError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{docketError}</div>
        )}
        {showDockets && <h2 className="text-sm font-semibold text-navy-900 mb-2">Category libraries</h2>}
        {showDockets && (
        <>
        {docketsLoading ? (
          <div className="mb-6 flex items-center text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading the library…
          </div>
        ) : dockets.length === 0 ? (
          <div className="mb-6 rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
            No libraries yet. A library is created per category and rebuilt by the strategy docket garden run.
          </div>
        ) : (
          <div className="mb-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Library</th>
                  <th className="text-left px-4 py-2.5 font-medium">Clients drawing on it</th>
                  <th className="text-left px-4 py-2.5 font-medium">Served</th>
                  <th className="text-right px-4 py-2.5 font-medium">Concepts</th>
                  <th className="text-right px-4 py-2.5 font-medium">Strategies</th>
                  <th className="text-right px-4 py-2.5 font-medium">Templates</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dockets.map((d) => (
                  <tr key={d.docket_id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <Link to={`/knowledge/creative-strategy/library/${encodeURIComponent(d.docket_id)}`} className="font-medium text-navy-900 hover:text-brand-teal">
                        {d.name}
                      </Link>
                      <div className="text-xs text-slate-400 font-mono">{d.scope}</div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{d.member_clients.join(', ') || '—'}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{d.pinned_version ? `${d.pinned_version} pinned` : (d.served_version ?? '—')}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{d.counts.buyer_concepts ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{d.counts.strategies ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{d.counts.templates ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}

        {showPacks && <h2 className="text-sm font-semibold text-navy-900 mb-2">Dockets</h2>}
        {showPacks && (
        <>
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Listing dockets…
          </div>
        ) : packs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            No dockets yet. A client's evidence is produced by the mining pass and published per client
            under <code className="text-xs bg-slate-100 px-1 rounded">creative_strategy/&lt;client_uid&gt;/v&lt;ts&gt;/</code>.
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Client</th>
                  <th className="text-left px-4 py-2.5 font-medium">Library</th>
                  <th className="text-left px-4 py-2.5 font-medium">Category scope</th>
                  <th className="text-left px-4 py-2.5 font-medium">Served version</th>
                  <th className="text-right px-4 py-2.5 font-medium">Concepts</th>
                  <th className="text-right px-4 py-2.5 font-medium">Strategies</th>
                  <th className="text-right px-4 py-2.5 font-medium">Templates</th>
                  <th className="text-right px-4 py-2.5 font-medium">Gaps</th>
                  <th className="text-left px-4 py-2.5 font-medium">Published</th>
                  <th className="text-left px-4 py-2.5 font-medium">Flag</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {packs.map((p) => (
                  <tr key={p.client_uid} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/knowledge/creative-strategy/dockets/${encodeURIComponent(p.client_uid)}`}
                        className="font-medium text-navy-900 hover:text-brand-teal"
                      >
                        {p.client_uid}
                      </Link>
                      {p.hidden_count > 0 && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                          <EyeOff size={10} /> {p.hidden_count} hidden
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {libraryOf(p.client_uid).length === 0 ? (
                        <span className="text-slate-400">{docketsLoading ? '…' : 'none'}</span>
                      ) : (
                        libraryOf(p.client_uid).map((d) => (
                          <Link
                            key={d.docket_id}
                            to={`/knowledge/creative-strategy/library/${encodeURIComponent(d.docket_id)}`}
                            className="block text-navy-900 hover:text-brand-teal"
                          >
                            {d.name}
                          </Link>
                        ))
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 max-w-xs truncate" title={p.scope?.paths?.join(' | ')}>
                      {p.scope?.paths?.[0] ?? '—'}
                      {p.scope?.levels && (
                        <span className="ml-2 text-xs text-slate-400">{p.scope.levels.join(' + ')}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-700">
                      {p.pinned_version ? (
                        <span className="inline-flex items-center gap-1 text-brand-teal">
                          <Pin size={11} /> {p.pinned_version}
                          <span className="text-slate-400 font-sans">(pinned; latest {p.latest_version})</span>
                        </span>
                      ) : (
                        <span>{p.latest_version ?? '—'} <span className="text-slate-400 font-sans">latest</span></span>
                      )}
                      {p.version_count > 1 && (
                        <span className="ml-2 text-slate-400 font-sans">{p.version_count} versions</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{p.counts.buyer_concepts ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{p.counts.strategies ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{p.counts.templates ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{p.counts.gaps ?? '—'}</td>
                    <td className="px-4 py-2.5 text-slate-600">{fmtDate(p.published_at)}</td>
                    <td className="px-4 py-2.5">
                      <span
                        className={clsx(
                          'text-[11px] font-medium rounded-full px-2 py-0.5 border',
                          p.enabled
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-slate-100 text-slate-500 border-slate-200',
                        )}
                        title="CREATIVE_STRATEGY_CONTEXT_CLIENTS on the API"
                      >
                        {p.enabled ? 'serving' : 'off'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}

        {showPacks && (
          <p className="mt-4 text-xs text-slate-500 max-w-3xl">
            A docket is evidence, not instruction: the pilot found no level of outside evidence predicted a client's own ad
            results. "Serving" means briefs and angles for that client are offered its docket when they run; the flag lives
            in the API environment, not here. Each version of a library or docket is a pack; pin a version or hide items on
            the client's docket page.
          </p>
        )}
      </div>
    </div>
  );
}
